import { Credential } from "lib/dataControler";
import { DataControlerContext } from "context/DataControlerContext";
import { useContext } from "react";
import Cookies from "js-cookie";
import {
  fetchWithParams,
  fetchPostWithParams,
  sendGetHttpRequest,
  sendHttpRequest,
} from "lib/fetcher";
import { SignalContext } from "context/SignalContext";
import { DB_STORE } from "lib/constants";
import { CameraInfo, CameraAlertRuleType, DbPicDataType } from "lib/types";
import { CameraStreamType } from "webrtc-player/common/base.js";
import { SinglePcManagerContext } from "context/SinglePcManagerContext";
import { CameraSnapUnitType } from "webrtc-player/webrtc_controller/data_controller";
import { isSupportSinglePcV2 } from "lib/utils";
import {
  DataWebRTC,
  DeviceSnapshotType,
} from "webrtc-player/webrtc/data_webrtc.js";
export type SnapshotDataTypes = {
  blob?: Blob;
  id: number;
  macAddress?: string;
  remoteIndex?: number;
  width?: number;
  height?: number;
  error?: any;
};

type ServiceCameraStreamType = CameraStreamType & {
  type: string;
  devId: string;
};

export const uploadSign = async (fileName) => {
  return await sendHttpRequest({
    method: "post",
    url: "/s3/temp_object",
    data: {
      filename: fileName,
    },
  });
};

export async function getIpSpeakers(orgId, siteId: number) {
  const token = Cookies.get("token");

  try {
    const data = await fetchWithParams(
      `/speaker/speakers?withAudio=true&siteId=${siteId}`,
      token,
      orgId,
    );
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getCameraDetail(
  orgId: string,
  cameraId: number,
  additionQuery = "",
) {
  const token = Cookies.get("token");

  try {
    const data = await fetchWithParams(
      `/camera/cameras/${cameraId}` + additionQuery,
      token,
      orgId,
    );
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getSiteCameras(
  orgId: string,
  siteId: number,
  nvrId: number | string = "",
  withAiSettings = false,
) {
  return sendGetHttpRequest(
    `/camera/cameras?siteIds=${siteId}&page.limit=600&nvrId=${nvrId}&withAiSettings=${withAiSettings}`,
    orgId,
  );
}

export async function getCameraRule(
  orgId: string,
  id: number,
  withAiSettings?: boolean,
) {
  return sendGetHttpRequest(
    `/alert/rules/camera/${id}?withAiSettings=${withAiSettings || false}`,
    orgId,
  );
}

export async function subCameraStatus(
  orgId: string,
  info: { ids: number[]; sessionId: string; types: string[] },
) {
  const token = Cookies.get("token");

  try {
    const data = await fetchPostWithParams(
      `/camera/cameras/sub`,
      token,
      orgId,
      info,
    );
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function unSubCameraStatus(
  orgId: string,
  info: { ids: number[]; sessionId: string; types: string[] },
) {
  const token = Cookies.get("token");

  try {
    const data = await fetchPostWithParams(
      `/camera/cameras/unsub`,
      token,
      orgId,
      info,
    );
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function useCommonService() {
  const { getControllerManagerWithWaiter } = useContext(DataControlerContext);
  const { getController } = useContext(SinglePcManagerContext);
  const { getDb } = useContext(SignalContext);
  const SnapshotRetryTimes = 3;

  function toUnitResolveDataV2(camera: DeviceSnapshotType) {
    return new Promise<SnapshotDataTypes>(async (resolve, reject) => {
      if (camera.snapshot.imageBlob) {
        // never happen
        resolve(void 0);
      } else if (
        camera.snapshot.imageBitmap &&
        camera.snapshot.imageBitmap.width > 0
      ) {
        const canvas = document.createElement("canvas");
        const width = (canvas.width = camera.snapshot.imageBitmap.width);
        const height = (canvas.height = camera.snapshot.imageBitmap.height);
        const context = canvas.getContext("bitmaprenderer");
        const newImageBitmap = await createImageBitmap(
          camera.snapshot.imageBitmap,
        );
        context.transferFromImageBitmap(newImageBitmap);
        canvas.toBlob(async (blob) => {
          resolve({
            blob,
            id: camera?.camera?.id,
            macAddress: camera?.camera?.macAddress,
            remoteIndex: camera?.camera?.remoteIndex,
            width,
            height,
          });
        });
      }
    });
  }

  // const takeSnapshotV3 = async function (
  //   nvrId: number,
  //   _cameras: CameraStreamType[],
  //   snapshotCallback?: (snapshots: SnapshotDataTypes[]) => void,
  //   orgId?: string,
  //   ignoreCache = false,
  // ) {
  //   const resMap = new Map<number | string, SnapshotDataTypes>();

  //   const controler = await getControllerManagerWithWaiter();
  //   const funCallback = (snapshot: DeviceSnapshotType) => {
  //     toUnitResolveDataV2(snapshot).then((unitResolveData) => {
  //       snapshotCallback?.([unitResolveData]);
  //     });
  //   };
  //   const startTakeSnapshots = async (
  //     nvrId: number,
  //     cameras: CameraStreamType[],
  //     retry = 0,
  //   ) => {
  //     console.log("start TakeSnap shots", nvrId, cameras, retry);
  //     let expiredCameras: CameraStreamType[] = [];
  //     if (!ignoreCache) {
  //       expiredCameras = await readDBSnapshot(_cameras, resMap);

  //       snapshotCallback?.(Array.from(resMap.values()));
  //       if (expiredCameras.length === 0) {
  //         return [];
  //       }
  //     } else {
  //       expiredCameras = _cameras;
  //     }

  //     return new Promise<SnapshotDataTypes[] | CameraStreamType[]>(
  //       (resolve, rejects) => {
  //         controler?.asyncGetDataController(nvrId, orgId).then((controller) => {
  //           const lastTime = Date.now();

  //           controller.deviceTakeSnapshots(
  //             expiredCameras,
  //             async (error, result) => {
  //               setTimeout(() => {
  //                 controller.webRTC.removeEventListener(
  //                   DataWebRTC.EVENT_DEVICE_SNAPSHOTS,
  //                   funCallback,
  //                 );
  //               }, 60 * 1000);
  //               if (!result.cameras || result.cameras.length == 0) {
  //                 return;
  //               }
  //               const errorCameras: CameraStreamType[] = [];
  //               for (const camera of result.cameras) {
  //                 if (!camera?.error) {
  //                   //const unitResolveData = await toUnitResolveData(camera);
  //                   resMap.set(
  //                     camera.camera.id ??
  //                       camera.camera.macAddress + camera.camera.remoteIndex,
  //                     {
  //                       id: camera.camera.id,
  //                     },
  //                   );
  //                 } else {
  //                   const cur: SnapshotDataTypes = {
  //                     id: camera?.camera?.id,
  //                     macAddress: camera?.camera?.macAddress,
  //                     remoteIndex: camera?.camera?.remoteIndex,
  //                     error: camera?.error,
  //                   };
  //                   if (retry >= SnapshotRetryTimes) {
  //                     resMap.set(
  //                       camera.camera.id ??
  //                         camera?.camera?.macAddress +
  //                           camera?.camera?.remoteIndex,
  //                       cur,
  //                     );
  //                     console.warn(
  //                       "takeSnapshot failed. nvrId:",
  //                       nvrId,
  //                       "cameraId:",
  //                       camera.camera?.id,
  //                     );
  //                   }
  //                   errorCameras.push(camera?.camera);
  //                 }
  //               }
  //               if (
  //                 resMap?.size === _cameras.length ||
  //                 retry >= SnapshotRetryTimes
  //               ) {
  //                 resolve(errorCameras);
  //                 //return;
  //               }
  //               if (retry < SnapshotRetryTimes && errorCameras.length > 0) {
  //                 console.warn(
  //                   "takeSnapshot retry. nvrId:",
  //                   nvrId,
  //                   "cmaeraIds:",
  //                   errorCameras.map(({ id }) => id)?.join(","),
  //                   "retry",
  //                   retry,
  //                 );
  //                 let interval = 1000 * 3 - (Date.now() - lastTime);
  //                 if (interval < 0) {
  //                   interval = 0;
  //                 }
  //                 setTimeout(() => {
  //                   startTakeSnapshots(nvrId, errorCameras, retry + 1).then(
  //                     (imageBlob) => {
  //                       resolve(imageBlob);
  //                     },
  //                   );
  //                 }, interval);
  //               }
  //             },
  //           );

  //           controller.webRTC.addEventListener(
  //             DataWebRTC.EVENT_DEVICE_SNAPSHOTS,
  //             funCallback,
  //           );
  //         });
  //       },
  //     );
  //   };

  //   return startTakeSnapshots(nvrId, _cameras);
  // };

  const getCredentials = async function (nvrIds: number[], orgId?) {
    // 去重
    nvrIds = nvrIds.filter(function (value, index, array) {
      return (
        array.indexOf(value) === index &&
        value !== null &&
        value !== undefined &&
        value !== 0
      );
    });

    if (nvrIds.length === 0) {
      return [];
    }

    const controler = await getControllerManagerWithWaiter();
    return new Promise((resolve: (res: Credential[]) => void, reject) => {
      controler
        ?.subscribeCredentialsReady(nvrIds, orgId)
        .then((credentials) => {
          resolve(credentials);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const readDBSnapshot = async (cameras: CameraStreamType[], resMap) => {
    const db = await getDb();
    for (const camera of cameras) {
      if (Number.isInteger(+camera.id)) {
        try {
          const data = await db.read(DB_STORE.SNAPSHOT, +camera.id);
          const snapshot = data as DbPicDataType;
          if (
            snapshot?.__updateTime &&
            snapshot?.__updateTime > Date.now() - 1000 * 60 * 60
          ) {
            resMap.set(+camera.id, {
              blob: snapshot.imageBlob,
              id: camera?.id,
              macAddress: camera?.macAddress,
              remoteIndex: camera?.remoteIndex,
              width: snapshot.width,
              height: snapshot.height,
            });
          }
        } catch (error) {
          console.error("read snapshot error:", error);
        }
      }
    }
    const expiredCameras = cameras?.filter(
      (camera) =>
        !resMap.has(camera.id ?? camera.macAddress + camera.remoteIndex),
    );
    return expiredCameras;
  };

  const takeSnapshotV4 = async function (
    nvrId: number,
    deviceIdV2: string,
    _cameras: CameraStreamType[],
    orgId: string,
    snapshotCallback?: (snapshots: SnapshotDataTypes[]) => void,
    ignoreCache = false, // force to take snapshot
  ) {
    const resMap = new Map<number | string, SnapshotDataTypes>();

    const controler = await getController(nvrId, deviceIdV2, orgId);
    const funCallback = (snapshot: DeviceSnapshotType) => {
      toUnitResolveDataV2(snapshot).then((unitResolveData) => {
        snapshotCallback?.([unitResolveData]);
      });
    };
    const startTakeSnapshots = async (
      cameras: CameraStreamType[],
      retry = 0,
    ) => {
      console.log("start TakeSnap shots", nvrId, deviceIdV2, cameras, retry);
      let expiredCameras: CameraStreamType[] = [];
      if (!ignoreCache) {
        expiredCameras = await readDBSnapshot(_cameras, resMap);

        resMap.size > 0 && snapshotCallback?.(Array.from(resMap.values()));
        if (expiredCameras.length === 0) {
          return [];
        }
      } else {
        expiredCameras = _cameras;
      }

      return new Promise<SnapshotDataTypes[] | CameraStreamType[]>(
        (resolve, rejects) => {
          const lastTime = Date.now();

          controler.deviceTakeSnapshots(
            expiredCameras,
            async (error, result) => {
              setTimeout(() => {
                controler.webRTC.removeEventListener(
                  DataWebRTC.EVENT_DEVICE_SNAPSHOTS,
                  funCallback,
                );
              }, 60 * 1000);
              if (!result.cameras || result.cameras.length == 0) {
                return;
              }
              const errorCameras: CameraStreamType[] = [];
              for (const camera of result.cameras) {
                if (!camera?.error) {
                  //const unitResolveData = await toUnitResolveData(camera);
                  resMap.set(
                    camera.camera.id ??
                      camera.camera.macAddress + camera.camera.remoteIndex,
                    {
                      id: camera.camera.id,
                    },
                  );
                } else {
                  const cur: SnapshotDataTypes = {
                    id: camera?.camera?.id,
                    macAddress: camera?.camera?.macAddress,
                    remoteIndex: camera?.camera?.remoteIndex,
                    error: camera?.error,
                  };
                  if (retry >= SnapshotRetryTimes) {
                    resMap.set(
                      camera.camera.id ??
                        camera?.camera?.macAddress +
                          camera?.camera?.remoteIndex,
                      cur,
                    );
                    console.warn(
                      "takeSnapshot failed. nvrId:",
                      nvrId,
                      'deviceIdV2:"',
                      deviceIdV2,
                      "cameraId:",
                      camera.camera?.id,
                    );
                  }
                  errorCameras.push(camera?.camera);
                }
              }
              if (
                resMap?.size === _cameras.length ||
                retry >= SnapshotRetryTimes
              ) {
                resolve(errorCameras);
                //return;
              }
              if (retry < SnapshotRetryTimes && errorCameras.length > 0) {
                console.warn(
                  "takeSnapshot retry. nvrId:",
                  nvrId,
                  'deviceIdV2:"',
                  deviceIdV2,
                  "cmaeraIds:",
                  errorCameras.map(({ id }) => id)?.join(","),
                  "retry",
                  retry,
                );
                let interval = 1000 * 3 - (Date.now() - lastTime);
                if (interval < 0) {
                  interval = 0;
                }
                setTimeout(() => {
                  startTakeSnapshots(errorCameras, retry + 1).then(
                    (imageBlob) => {
                      resolve(imageBlob);
                    },
                  );
                }, interval);
              }
            },
          );

          controler.webRTC.addEventListener(
            DataWebRTC.EVENT_DEVICE_SNAPSHOTS,
            funCallback,
          );
        },
      );
    };

    return startTakeSnapshots(_cameras);
  };

  const takeSnapshot = async function (
    cameras: CameraInfo[] | CameraAlertRuleType[],
    orgId: string,
    snapshotCallback?: (snapshots: SnapshotDataTypes[]) => void,
    ignoreCache = false,
  ) {
    const fixCameras = cameras.map((item) => {
      let streamId = 0;
      const id = +item.id || +item.cameraId || undefined; // undefined when creating new camera
      const devId = item.devId || item.deviceId;
      if (item.capabilities?.streamNum > 1) streamId = 1;
      return {
        nvrId: item.nvrId,
        streamId,
        remoteIndex: item.remoteIndex,
        id,
        devId,
        type: item.type,
        macAddress: item.macAddress,
      };
    });
    const result = [];
    // nvr cameras
    const nvrCameras = fixCameras.filter((item) => item.type !== "camera_edge");
    const dataNvrsUnunique = nvrCameras.map((item) => item.nvrId);
    const dataNvrs = dataNvrsUnunique.filter(function (value, index, array) {
      return array.indexOf(value) === index;
    });
    dataNvrs?.forEach((nvrId) => {
      const camerasForNvr = nvrCameras?.filter((item) => item.nvrId == nvrId);

      const chunkCameras = [];
      const chunkSize = 16;
      for (let i = 0; i < camerasForNvr?.length; i += chunkSize) {
        chunkCameras.push(camerasForNvr?.slice(i, i + chunkSize));
      }

      chunkCameras.forEach(async (chunk) => {
        const errCameras = await takeSnapshotChunk(
          nvrId,
          chunk,
          orgId,
          snapshotCallback,
          ignoreCache,
          chunk?.at(0)?.devId,
        );
        result.push(...errCameras);
      });
    });

    // // edge cameras
    const edgeCameras: ServiceCameraStreamType[] = fixCameras.filter(
      (item) => item.type === "camera_edge",
    );

    edgeCameras?.forEach(async (edgeCamera) => {
      const errCameras = await takeSnapshotChunk(
        undefined,
        [edgeCamera],
        orgId,
        snapshotCallback,
        ignoreCache,
        edgeCamera?.devId,
      );
      result.push(...errCameras);
    });
    return result;
  };

  const takeSnapshotChunk = async function (
    nvrId: number,
    _cameras: CameraStreamType[],
    orgId: string,
    snapshotCallback?: (snapshots: SnapshotDataTypes[]) => void,
    ignoreCache = false,
    deviceIdV2?: string,
  ) {
    return takeSnapshotV4(
      nvrId,
      deviceIdV2,
      _cameras,
      orgId,
      snapshotCallback,
      ignoreCache,
    );
  };

  return {
    // takeSnapshotV3,
    takeSnapshot,
    getCredentials,
  };
}
